import { ElementCategoryID } from '../../models/element-categories.model';
import { IElement } from '../../models/project.model';
import { EnergyProductId } from '../../templates/categories/energy/energy.model';
import { SupportedConversionUnits } from '../conversion-factors.helpers';

/**
 * How much of each energy carrier is used to provide energy to the building.
 */
export type EnergyCarrierMix = Record<EnergyCarrier, number>;

/**
 * Different energy carriers that can be used to provide energy to the building.
 */
export enum EnergyCarrier {
  Oil = 'oil',
  Gas = 'gas',
  Electricity = 'electricity',
  /**
   * Solids, liquids and gases that are renewable
   */
  Bio = 'bio',
  DistrictHeating = 'district_heating',
  DistrictCooling = 'district_cooling',
}

export const energyCarrierNames: Record<EnergyCarrier, string> = {
  [EnergyCarrier.Oil]: 'Oil',
  [EnergyCarrier.Gas]: 'Gas',
  [EnergyCarrier.Electricity]: 'Electricity',
  [EnergyCarrier.Bio]: 'Bio',
  [EnergyCarrier.DistrictHeating]: 'District heating',
  [EnergyCarrier.DistrictCooling]: 'District cooling',
};

export const defaultEnergyMix: EnergyCarrierMix = {
  [EnergyCarrier.Oil]: 0,
  [EnergyCarrier.Gas]: 0,
  [EnergyCarrier.Electricity]: 0,
  [EnergyCarrier.Bio]: 0,
  [EnergyCarrier.DistrictHeating]: 0,
  [EnergyCarrier.DistrictCooling]: 0,
};

/**
 * Weight factors for different energy carriers. Use to reward/punish different energy carriers when calculating the primary energy value.
 * From page 139: https://www.boverket.se/resources/constitutiontextstore/bbr/PDF/konsoliderad_bbr_2011-6.pdf#9_12_definitioner
 */
export const energyCarrierWeightFactors: EnergyCarrierMix = Object.freeze({
  [EnergyCarrier.Oil]: 1.8,
  [EnergyCarrier.Gas]: 1.8,
  [EnergyCarrier.Electricity]: 1.8,
  [EnergyCarrier.DistrictHeating]: 0.7,
  [EnergyCarrier.Bio]: 0.6,
  [EnergyCarrier.DistrictCooling]: 0.6,
});

export const energyCarrierProductIds: Record<EnergyCarrier, EnergyProductId[]> =
  Object.freeze({
    [EnergyCarrier.Oil]: [
      EnergyProductId.Diesel,
      EnergyProductId.Petrol,
      EnergyProductId.FuelOil1,
      EnergyProductId.FuelOil2,
      EnergyProductId.DieselReductionObligation,
      EnergyProductId.PetrolReductionObligation,
    ],
    [EnergyCarrier.Gas]: [EnergyProductId.Gasol],
    [EnergyCarrier.Electricity]: [EnergyProductId.Electricity],
    [EnergyCarrier.DistrictHeating]: [EnergyProductId.DistrictHeating],
    [EnergyCarrier.Bio]: [
      EnergyProductId.Biogas,
      EnergyProductId.HVO100,
      EnergyProductId.FAME100,
      EnergyProductId.ED95,
      EnergyProductId.E85,
      EnergyProductId.WoodPellets,
    ],
    [EnergyCarrier.DistrictCooling]: [EnergyProductId.DistrictCooling],
  });

/**
 * Where goes the energy? Is it for heating, cooling or building energy (fans, lights etc)
 */
export enum EnergyConsumer {
  /**
   * Heating energy (uppvärmning)
   */
  Heating = 'heating',

  /**
   * Cooling energy
   */
  Cooling = 'cooling',

  /**
   * Tapwater energy (varmvattenenergi)
   */
  Tapwater = 'tapwater',

  /**
   * Building operation energy (fastighetsenergi Ef)
   */
  Building = 'building',
}

export const energyConsumerNames: Record<EnergyConsumer, string> = {
  [EnergyConsumer.Heating]: 'Space heating',
  [EnergyConsumer.Cooling]: 'Space cooling',
  [EnergyConsumer.Tapwater]: 'Tapwater heating',
  [EnergyConsumer.Building]: 'Building services',
};

export type EnergyConsumptionRecord = Record<EnergyConsumer, EnergyCarrierMix>;

export type EnergyByCarrierByConsumer = Record<
  EnergyCarrier,
  Record<EnergyConsumer, number>
>;

export const defaultEnergyConsumptionRecord: EnergyConsumptionRecord = {
  [EnergyConsumer.Heating]: { ...defaultEnergyMix },
  [EnergyConsumer.Cooling]: { ...defaultEnergyMix },
  [EnergyConsumer.Tapwater]: { ...defaultEnergyMix },
  [EnergyConsumer.Building]: { ...defaultEnergyMix },
};

export const EnergyConsumerElementCategory = Object.freeze([
  ElementCategoryID.Energy,
  ElementCategoryID.AirHandlingUnit,
  ElementCategoryID.Heating,
  ElementCategoryID.Cooling,
] as const);

export type IEnergyConsumerElement = IElement & {
  category_id: (typeof EnergyConsumerElementCategory)[number];
};

/**
 * Supported units for to extract as energy mix
 */
export type EnergyMixUnit = Extract<
  SupportedConversionUnits,
  'kWh' | 'co2e_B6' | 'sek_B6'
>;
